import type React from "react";
import { cn } from "@/lib/utils";

/** Props every product shot carries: decorative, never focusable. */
export const shotProps = { "aria-hidden": true, inert: true } as const;

/** Marks sample data as an example so nobody reads it as a customer reference. */
export function ExampleTag({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<span
			className={cn(
				"inline-flex shrink-0 items-center gap-1 rounded-full border bg-background/80 px-2 py-0.5 font-mono text-[9px] font-medium tracking-[0.05em] whitespace-nowrap text-muted-foreground uppercase",
				className,
			)}
		>
			<span className="size-1 rounded-full bg-signal" />
			{children}
		</span>
	);
}

/** Desktop browser window, light product theme. Content is laid out fluidly. */
export function BrowserFrame({
	host,
	tag,
	children,
	className,
	contentClassName,
}: {
	host: string;
	tag: string;
	children: React.ReactNode;
	className?: string;
	contentClassName?: string;
}) {
	return (
		<div
			{...shotProps}
			className={cn(
				"paper shot-shadow overflow-hidden rounded-xl border select-none",
				className,
			)}
		>
			<div className="flex h-8 items-center gap-3 border-b bg-muted/60 px-3">
				<div className="flex gap-1.5">
					<span className="size-2.5 rounded-full bg-foreground/15" />
					<span className="size-2.5 rounded-full bg-foreground/15" />
					<span className="size-2.5 rounded-full bg-foreground/15" />
				</div>
				<div className="mx-auto flex h-5 min-w-0 max-w-[60%] flex-1 items-center justify-center rounded-md bg-background px-3 font-mono text-[10px] text-muted-foreground">
					<span className="truncate">{host}</span>
				</div>
				<ExampleTag>{tag}</ExampleTag>
			</div>
			<div className={cn("relative", contentClassName)}>{children}</div>
		</div>
	);
}

/** Phone with status bar and home indicator, light product theme. */
export function PhoneFrame({
	tag,
	children,
	className,
}: {
	/** Omit when a neighbouring frame already carries the tag. */
	tag?: string;
	children: React.ReactNode;
	className?: string;
}) {
	return (
		<div
			{...shotProps}
			className={cn(
				"paper shot-shadow relative aspect-[9/19] w-full max-w-[17rem] rounded-[2.6rem] bg-foreground p-[7px] select-none",
				className,
			)}
		>
			<div className="relative flex h-full flex-col overflow-hidden rounded-[2.2rem] bg-background">
				<div className="flex h-9 shrink-0 items-center justify-between px-6 pt-1 text-[11px] font-semibold tabular-nums">
					<span>9:41</span>
					<span className="absolute top-3 left-1/2 size-2.5 -translate-x-1/2 rounded-full bg-foreground" />
					{tag ? (
						<ExampleTag className="px-1.5 text-[8px]">{tag}</ExampleTag>
					) : (
						<span className="flex gap-0.5">
							<span className="h-2 w-1 rounded-sm bg-foreground/70" />
							<span className="h-2 w-1 rounded-sm bg-foreground/70" />
							<span className="h-2 w-1 rounded-sm bg-foreground/30" />
						</span>
					)}
				</div>
				<div className="relative min-h-0 flex-1">{children}</div>
				<div className="flex h-5 shrink-0 items-center justify-center">
					<span className="h-1 w-24 rounded-full bg-foreground/80" />
				</div>
			</div>
		</div>
	);
}
